// Local Storage Manager - Barrel re-export
// Original 1,676-line file split into domain modules:
//   storage-types.ts     - All interfaces and type definitions
//   storage-constants.ts - Default values, templates, pricing constants
//   storage-permissions.ts - Permission helper functions

import { logger } from './logger';

export * from './storage-types';
export * from './storage-constants';
export * from './storage-permissions';
// NOTE: The localStorage managers are defined locally below and deliberately
// NOT split out or re-exported from a separate module — duplicate bindings
// caused TDZ errors in webpack.

// Import types needed by local declarations
import type { Owner, Driver, KontantEdit, KontantEditsStore, CompanyInfo, CarInfo } from './storage-types';
import { isAnyAdmin } from './storage-permissions';

// NOTE: All types, interfaces, constants, and permission functions are defined
// in their respective sub-modules (storage-types.ts, storage-constants.ts,
// storage-permissions.ts) and re-exported via the wildcard exports above.
// Do NOT redefine them here.

// NOTE: All types (BankTransaction, PowerOfficeVoucher, ReconcileMatch, etc.)
// are defined in storage-types.ts and re-exported via 'export * from ./storage-types'.
// All constants (DEFAULT_UPLOAD_DESTINATIONS, etc.) are in storage-constants.ts
// and re-exported via 'export * from ./storage-constants'.
// Do NOT redefine them here — duplicate exports cause webpack TDZ errors.

// Current user context
let currentOwner: Owner | null = null;

// Storage Keys
const STORAGE_KEYS = {
  DRIVERS: 'voss_taxi_drivers',
  COMPANY_INFO: 'voss_taxi_company_info',
  THEME: 'voss_taxi_theme',
  LANGUAGE: 'voss_taxi_language',
  KONTANT_EDITS: 'voss_taxi_kontant_edits',
  CAR_INFO: 'voss_taxi_car_info',
  OWNERS: 'voss_taxi_owners', // Global list of all owners
  CURRENT_OWNER: 'voss_taxi_current_owner', // Currently logged-in owner
} as const;

// Helper to get user-specific key
function getUserKey(baseKey: string): string {
  if (!currentOwner) return baseKey; // Fallback to global key for backward compatibility
  return `${baseKey}_user_${currentOwner.id}`;
}

// Generic Storage Functions
// Use var to avoid TDZ issues with webpack module concatenation
export var storage = {
  // Get data from localStorage
  get: <T>(key: string, defaultValue: T): T => {
    if (typeof window === 'undefined') return defaultValue;

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      logger.error('storage', 'STORAGE_GET_ERROR', 'Error reading from localStorage', { key, error: String(error) });
      return defaultValue;
    }
  },

  // Set data to localStorage
  set: <T>(key: string, value: T): void => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      logger.error('storage', 'STORAGE_SET_ERROR', 'Error writing to localStorage', { key, error: String(error) });
    }
  },

  // Remove data from localStorage
  remove: (key: string): void => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      logger.error('storage', 'STORAGE_REMOVE_ERROR', 'Error removing localStorage key', { key, error: String(error) });
    }
  },

  // Clear all localStorage
  clear: (): void => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.clear();
    } catch (error) {
      logger.error('storage', 'STORAGE_CLEAR_ERROR', 'Error clearing localStorage', { error: String(error) });
    }
  },
};

// Current Owner Manager - canonical: manages auth state in localStorage
export var currentOwnerManager = {
  set: (owner: Owner | null): void => {
    currentOwner = owner;
    if (owner) {
      owner.last_login = new Date().toISOString();
      storage.set(STORAGE_KEYS.CURRENT_OWNER, owner);
    } else {
      storage.remove(STORAGE_KEYS.CURRENT_OWNER);
    }
  },

  get: (): Owner | null => {
    if (currentOwner) return currentOwner;
    currentOwner = storage.get<Owner | null>(STORAGE_KEYS.CURRENT_OWNER, null);
    return currentOwner;
  },

  clear: (): void => {
    currentOwner = null;
    storage.remove(STORAGE_KEYS.CURRENT_OWNER);
  },

  isLoggedIn: (): boolean => {
    return currentOwnerManager.get() !== null;
  },

  isAdmin: (): boolean => {
    const owner = currentOwnerManager.get();
    return isAnyAdmin(owner);
  },

  getLoyver: (): string[] => {
    const owner = currentOwnerManager.get();
    return owner?.loyver || [];
  },
};

// Owners Manager (Global list of all owners)
export var ownersManager = {
  getAll: (): Owner[] => storage.get(STORAGE_KEYS.OWNERS, []),

  save: (owners: Owner[]): void => storage.set(STORAGE_KEYS.OWNERS, owners),

  findByEmail: (email: string): Owner | undefined => {
    return ownersManager.getAll().find(o => o.email === email);
  },

  findById: (id: string): Owner | undefined => {
    return ownersManager.getAll().find(o => o.id === id);
  },

  createOrUpdate: (owner: Owner): void => {
    const owners = ownersManager.getAll();
    const index = owners.findIndex(o => o.id === owner.id);
    if (index >= 0) {
      owners[index] = { ...owners[index], ...owner };
    } else {
      owners.push(owner);
    }
    ownersManager.save(owners);
  },

  addLoyve: (ownerId: string, loyve: string): void => {
    const owners = ownersManager.getAll();
    const owner = owners.find(o => o.id === ownerId);
    if (owner && !owner.loyver.includes(loyve)) {
      owner.loyver.push(loyve);
      ownersManager.save(owners);

      // Update current owner if it's the same
      if (currentOwner?.id === ownerId) {
        currentOwner.loyver = owner.loyver;
        currentOwnerManager.set(currentOwner);
      }
    }
  },

  removeLoyve: (ownerId: string, loyve: string): void => {
    const owners = ownersManager.getAll();
    const owner = owners.find(o => o.id === ownerId);
    if (owner) {
      owner.loyver = owner.loyver.filter(l => l !== loyve);
      ownersManager.save(owners);

      // Update current owner if it's the same
      if (currentOwner?.id === ownerId) {
        currentOwner.loyver = owner.loyver;
        currentOwnerManager.set(currentOwner);
      }
    }
  },

  delete: (id: string): void => {
    const owners = ownersManager.getAll().filter(o => o.id !== id);
    ownersManager.save(owners);
  },

  // Check if any admin exists
  hasAdmin: (): boolean => {
    return ownersManager.getAll().some(o => isAnyAdmin(o));
  },

  // Get first-time admin setup flag
  isFirstTimeSetup: (): boolean => {
    return !ownersManager.hasAdmin();
  },

  // Approve owner (admin only)
  approveOwner: (ownerId: string): void => {
    const owners = ownersManager.getAll();
    const owner = owners.find(o => o.id === ownerId);
    if (owner) {
      owner.approved = true;
      ownersManager.save(owners);
    }
  },

  // Get pending owners (not approved)
  getPendingOwners: (): Owner[] => {
    return ownersManager.getAll().filter(o => !o.approved && !isAnyAdmin(o));
  },

  // Get approved owners
  getApprovedOwners: (): Owner[] => {
    return ownersManager.getAll().filter(o => o.approved || isAnyAdmin(o));
  },
};

// Specific Data Managers (now user-scoped)
export var driversManager = {
  getAll: (): Driver[] => storage.get(getUserKey(STORAGE_KEYS.DRIVERS), []),

  save: (drivers: Driver[]): void => storage.set(getUserKey(STORAGE_KEYS.DRIVERS), drivers),

  add: (driver: Driver): void => {
    const drivers = driversManager.getAll();
    drivers.push(driver);
    driversManager.save(drivers);
  },

  update: (id: string, updatedDriver: Partial<Driver>): void => {
    const drivers = driversManager.getAll();
    const index = drivers.findIndex(d => d.id === id);
    if (index !== -1) {
      drivers[index] = { ...drivers[index], ...updatedDriver };
      driversManager.save(drivers);
    }
  },

  delete: (id: string): void => {
    const drivers = driversManager.getAll().filter(d => d.id !== id);
    driversManager.save(drivers);
  },
};

export var companyInfoManager = {
  get: (): CompanyInfo => storage.get(getUserKey(STORAGE_KEYS.COMPANY_INFO), {
    name: '',
    orgnr: '',
    address: '',
  }),

  save: (info: CompanyInfo): void => storage.set(getUserKey(STORAGE_KEYS.COMPANY_INFO), info),

  /**
   * Update company info from Brønnøysundregistrene data
   * Preserves any user-edited fields while updating from official source
   */
  updateFromBrreg: (brregData: {
    orgNumber: string;
    companyName: string;
    companyTypeCode?: string | null;
    companyType?: string | null;
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
    municipality?: string | null;
    municipalityCode?: string | null;
    country?: string | null;
    industryCode?: string | null;
    industryDescription?: string | null;
    website?: string | null;
    registrationDate?: string | null;
    foundingDate?: string | null;
    employees?: number | null;
  }): CompanyInfo => {
    const current = companyInfoManager.get();

    const updated: CompanyInfo = {
      ...current,
      name: brregData.companyName,
      orgnr: brregData.orgNumber,
      address: brregData.street || current.address || '',
      companyTypeCode: brregData.companyTypeCode || undefined,
      companyType: brregData.companyType || undefined,
      postalCode: brregData.postalCode || undefined,
      city: brregData.city || undefined,
      municipality: brregData.municipality || undefined,
      municipalityCode: brregData.municipalityCode || undefined,
      country: brregData.country || undefined,
      industryCode: brregData.industryCode || undefined,
      industryDescription: brregData.industryDescription || undefined,
      website: brregData.website || undefined,
      registrationDate: brregData.registrationDate || undefined,
      foundingDate: brregData.foundingDate || undefined,
      employees: brregData.employees || undefined,
      brregVerified: true,
      brregVerifiedAt: new Date().toISOString(),
    };

    companyInfoManager.save(updated);
    return updated;
  },
};

export var themeManager = {
  // Returns the stored preference: 'system', 'dark', or 'light'
  getPreference: (): 'system' | 'dark' | 'light' => storage.get(STORAGE_KEYS.THEME, 'system'),

  // Returns the effective theme based on preference (resolves 'system' to actual theme)
  get: (): 'dark' | 'light' => {
    const pref = themeManager.getPreference();
    if (pref === 'system') {
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'light';
    }
    return pref;
  },

  set: (theme: 'system' | 'dark' | 'light'): void => {
    storage.set(STORAGE_KEYS.THEME, theme);
    const effective = theme === 'system'
      ? (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', effective);
    }
  },

  toggle: (): 'dark' | 'light' => {
    const current = themeManager.get();
    const newTheme = current === 'dark' ? 'light' : 'dark';
    themeManager.set(newTheme);
    return newTheme;
  },

  initialize: (): void => {
    if (typeof document !== 'undefined') {
      const effective = themeManager.get();
      document.documentElement.setAttribute('data-theme', effective);
    }
  },
};

export var languageManager = {
  get: (): string => storage.get(STORAGE_KEYS.LANGUAGE, 'en'),

  set: (language: string): void => storage.set(STORAGE_KEYS.LANGUAGE, language),
};

// Kontant Edits Manager (for Lønn subtab) - now user-scoped
export var kontantEditsManager = {
  // Get the full store
  getStore: (): KontantEditsStore => {
    return storage.get<KontantEditsStore>(getUserKey(STORAGE_KEYS.KONTANT_EDITS), {
      next_note_number: 1,
      edits: [],
    });
  },

  // Save the full store
  saveStore: (store: KontantEditsStore): void => {
    storage.set(getUserKey(STORAGE_KEYS.KONTANT_EDITS), store);
  },

  // Get all edits
  getAll: (): KontantEdit[] => {
    return kontantEditsManager.getStore().edits;
  },

  // Find edit by (loyve, skiftnr, kvitteringsnr)
  findEdit: (loyve: string, skiftnr: string, kvitteringsnr: string | null = null): KontantEdit | undefined => {
    const store = kontantEditsManager.getStore();
    return store.edits.find(
      e => e.loyve === loyve && e.skiftnr === skiftnr && e.kvitteringsnr === kvitteringsnr
    );
  },

  // Find all edits for a specific shift (regardless of kvitteringsnr)
  findEditsForShift: (loyve: string, skiftnr: string): KontantEdit[] => {
    const store = kontantEditsManager.getStore();
    return store.edits.filter(
      e => e.loyve === loyve && e.skiftnr === skiftnr
    );
  },

  // Create or update edit
  // Returns the note_number
  createOrUpdateEdit: (params: {
    loyve: string;
    skiftnr: string;
    kvitteringsnr?: string | null;
    driver_id: string;
    date?: string;
    original_kontant: number;
    edited_kontant: number;
    reason: string;
    created_by?: string;
  }): number => {
    const store = kontantEditsManager.getStore();
    const kvitteringsnr = params.kvitteringsnr ?? null;

    const existing = store.edits.find(
      e => e.loyve === params.loyve &&
           e.skiftnr === params.skiftnr &&
           e.kvitteringsnr === kvitteringsnr
    );

    if (existing) {
      // Update existing edit (note_number stays the same!)
      existing.edited_kontant = params.edited_kontant;
      existing.difference = params.edited_kontant - params.original_kontant;
      existing.reason = params.reason;
      existing.updated_at = new Date().toISOString();
      if (params.created_by) {
        existing.created_by = params.created_by;
      }
      kontantEditsManager.saveStore(store);
      return existing.note_number;
    } else {
      // Create new edit with new note_number
      const newEdit: KontantEdit = {
        note_number: store.next_note_number,
        loyve: params.loyve,
        skiftnr: params.skiftnr,
        kvitteringsnr: kvitteringsnr,
        driver_id: params.driver_id,
        date: params.date,
        original_kontant: params.original_kontant,
        edited_kontant: params.edited_kontant,
        difference: params.edited_kontant - params.original_kontant,
        reason: params.reason,
        created_at: new Date().toISOString(),
        created_by: params.created_by,
      };

      store.edits.push(newEdit);
      store.next_note_number += 1;
      kontantEditsManager.saveStore(store);
      return newEdit.note_number;
    }
  },

  // Delete edit by (loyve, skiftnr, kvitteringsnr)
  deleteEdit: (loyve: string, skiftnr: string, kvitteringsnr: string | null = null): void => {
    const store = kontantEditsManager.getStore();
    store.edits = store.edits.filter(
      e => !(e.loyve === loyve && e.skiftnr === skiftnr && e.kvitteringsnr === kvitteringsnr)
    );
    kontantEditsManager.saveStore(store);
  },

  // Get edits for a specific driver
  getByDriver: (driverId: string): KontantEdit[] => {
    return kontantEditsManager.getAll().filter(e => e.driver_id === driverId);
  },

  // Get edit by note_number
  getByNoteNumber: (noteNumber: number): KontantEdit | undefined => {
    return kontantEditsManager.getAll().find(e => e.note_number === noteNumber);
  },
};

// Car Info Manager (user-scoped) - Manage taxi/vehicle information
export var carInfoManager = {
  getAll: (): CarInfo[] => storage.get(STORAGE_KEYS.CAR_INFO, []),

  save: (carInfos: CarInfo[]): void => storage.set(STORAGE_KEYS.CAR_INFO, carInfos),

  add: (carInfo: Omit<CarInfo, 'id' | 'created_at'>): CarInfo => {
    const carInfos = carInfoManager.getAll();
    const newCarInfo: CarInfo = {
      ...carInfo,
      id: `car_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      created_at: new Date().toISOString(),
      created_by: currentOwner?.id,
    };
    carInfos.push(newCarInfo);
    carInfoManager.save(carInfos);
    return newCarInfo;
  },

  update: (id: string, updatedCarInfo: Partial<CarInfo>): void => {
    const carInfos = carInfoManager.getAll();
    const index = carInfos.findIndex(c => c.id === id);
    if (index !== -1) {
      carInfos[index] = {
        ...carInfos[index],
        ...updatedCarInfo,
        updated_at: new Date().toISOString(),
      };
      carInfoManager.save(carInfos);
    }
  },

  delete: (id: string): void => {
    const carInfos = carInfoManager.getAll().filter(c => c.id !== id);
    carInfoManager.save(carInfos);
  },

  findByLoyve: (loyve: string): CarInfo | undefined => {
    return carInfoManager.getAll().find(c => c.loyve === loyve);
  },

  // Get car info filtered by user's assigned løyver (non-admins only see their cars)
  getAllForCurrentUser: (): CarInfo[] => {
    const allCarInfos = carInfoManager.getAll();
    const owner = currentOwnerManager.get();

    // Admins see all cars
    if (isAnyAdmin(owner)) {
      return allCarInfos;
    }

    // Non-admins only see cars matching their assigned løyver
    if (owner && owner.loyver.length > 0) {
      return allCarInfos.filter(car =>
        owner.loyver.includes(car.loyve) || owner.loyver.includes(car.loyve_nummer)
      );
    }

    // No user or no løyver assigned - return empty
    return [];
  },
};

// NOTE: ownersManager, driversManager, companyInfoManager, carInfoManager and
// kontantEditsManager are legacy localStorage managers. Production code should
// use firestoreXxxManager equivalents from './storage-firestore' instead.
// These remain for offline fallback.
